import React, { useMemo } from 'react';
import { gql } from '@apollo/client';
import { VariantCellProductPriceFragment } from '../../../../../generated/types/VariantCellProductPriceFragment';
import { getActiveVariantBy } from '../../../../../lib/getActiveVariantBy';
import { getRoundedMarkdownPercent } from '../../../../../lib/variantUtils/getRoundedMarkdownPercent';
import { formatPrice } from '../../../../../lib/formatPrice';

import './VariantCellProductPrice.scss';

export const VARIANT_CELL_PRODUCT_PRICE_VARIANT_FRAGMENT = gql`
  fragment VariantCellProductPriceVariantFragment on VariantData {
    id
    prices(country_code: $countryCode) {
      currentPrice: current_price {
        price
        currency
      }
      originalPrice: original_price {
        price
        currency
      }
      depositPrice: deposit_price {
        price
        currency
      }
    }
  }
`;

export const VARIANT_CELL_PRODUCT_PRICE_FRAGMENT = gql`
  fragment VariantCellProductPriceFragment on Variant {
    id
    masterVariant: master_variants_data {
      ...VariantCellProductPriceVariantFragment
    }
    otherVariants: other_variants_data {
      ...VariantCellProductPriceVariantFragment
    }
  }
  ${VARIANT_CELL_PRODUCT_PRICE_VARIANT_FRAGMENT}
`;

interface Props {
  variant: VariantCellProductPriceFragment;
  activeVariantId: string;
  showSaleSavings?: boolean;
}

export const VariantCellProductPrice: React.FC<Props> = ({
  variant,
  activeVariantId,
  children
}) => {
  const {
    prices: { currentPrice, originalPrice, depositPrice }
  } = useMemo(() => getActiveVariantBy(variant, 'id', activeVariantId), [activeVariantId, variant]);

  const showOriginalPrice =
    !!originalPrice && !!currentPrice && originalPrice.price !== currentPrice.price;

  const markdownPercent = getRoundedMarkdownPercent({
    currentPrice: parseFloat(currentPrice?.price || '0.0'),
    originalPrice: parseFloat(originalPrice?.price || '0.0')
  });

  return (
    <div className="VariantCellProductPrice">
      {showOriginalPrice && originalPrice?.price && (
        <span className="VariantCellProductPrice__original">
          {formatPrice(originalPrice.currency, originalPrice.price)}
        </span>
      )}

      {currentPrice?.price && (
        <span className="VariantCellProductPrice__current-price">
          {formatPrice(currentPrice.currency, currentPrice.price)}
        </span>
      )}

      {depositPrice?.price && (
        <span className="VariantCellProductPrice__deposit">{`(${formatPrice(
          depositPrice.currency,
          depositPrice.price
        )} Deposit)`}</span>
      )}

      {showOriginalPrice && markdownPercent > 0 && (
        <span className="VariantCellProductPrice__markdown-percent">Save {markdownPercent}%</span>
      )}

      {children && <div>{children}</div>}
    </div>
  );
};
